import { init, FeatureFlag } from '@goldcast/feature-flags';

import { deleteCookie, getCookieValue } from './localStorage.service';
import loggerService from './logger.service';
import { generatePersistentUUID } from './utils';

import { APP_ENVIRONMENT } from '@/constants';
import store from '@/store';

export const handlePreProdChange = (value: boolean) => {
  loggerService.warn('USE_PRE_PROD changed to', value);
  if (!value) {
    const isPreProd = getCookieValue('preProd');
    if (isPreProd) {
      deleteCookie('preProd');
      loggerService.info('[PAGE_RELOAD] Reloading after USE_PRE_PROD flag disabled (preProd cookie cleared)');
      window.location.reload();
    }
  }
};

export const handleInHouseSimuliveChange = (value: boolean) => {
  loggerService.warn('USE_INHOUSE_SIMULIVE changed to', value);
  store.commit('event/setUseInHouseSimulive', value, { root: true });

  if (!value) {
    store.dispatch('simuliveroom/destroySimuliveBroadcastPlaybackContext', null, {
      root: true
    });
  }
};

const changeUsePPTAnimations = (value: boolean) => {
  loggerService.warn('Use_PPT_Animations changed to', value);
  store.commit('event/stage/setUsePPTAnimations', value);
};

const handleUseHlsGuard = (value: boolean) => {
  loggerService.warn('Use_Hls_Guard changed to', value);
  store.commit('event/setUseHlsGuard', value);
};

const handleUseSpeakerActivity = (value: boolean) => {
  loggerService.warn('Use_Speaker_Activity changed to', value);
  store.commit('event/setUseSpeakerActivity', value);
};

const handleEventHeartbeatFastEnabled = (value: boolean) => {
  loggerService.warn('isEventHeartbeatFastEnabled changed to', value);
  store.commit('event/setEventHeartbeatFastEnabled', value);
};

const handleUseRecordingPlayPause = (value: boolean) => {
  loggerService.warn('Use_Recording_Play_Pause changed to', value);
  store.commit('event/setUseRecordingPlayPause', value);
};

const handleUsePentestFlow = (value: boolean) => {
  loggerService.warn('Use_Pentest_flow changed to', value);
  store.commit('event/setUsePentestFlow', value);
};

const handleUseEventLocaleForDateTimeFormattingChange = (value: boolean) => {
  loggerService.warn('useEventLocaleForDateTimeFormatting changed to', value);
  store.commit('event/setUseEventLocaleForDateTimeFormatting', value);
};

const handleUseHideSidebar = (value: boolean) => {
  loggerService.warn('Use_Hide_Sidebar changed to', value);
  store.commit('event/setUseHideSidebar', value);
};

const handleUse1080VideoQuality = (value: boolean) => {
  loggerService.warn('USE_1080_VIDEO_QUALITY changed to', value);
  store.commit('event/setUse1080VideoQuality', value);
};

const handleUseSkip480 = (value: boolean) => {
  loggerService.warn('Use_Skip_480 changed to', value);
  store.commit('event/setUseSkip480', value);
  // Flags arrive after loadEvent has already validated the stored preference, so re-run it
  // to migrate a persisted 480p selection once the flag turns on.
  store.dispatch('event/validateStoredVideoQuality');
};

const handleUseCloudProxyFlow = (value: boolean) => {
  loggerService.warn('Use_Cloud_Proxy_Flow changed to', value);
  store.commit('event/setUseCloudProxyFlow', value);
};

const handleEnableInhouseAddToCalendar = (value: boolean) => {
  loggerService.warn('enableInhouseAddToCalendar changed to', value);
  store.commit('event/setEnableInhouseAddToCalendar', value);
};

const handleUseCDNEndpoint = (value: boolean) => {
  loggerService.warn('Use_CDN_Endpoint changed to', value);
  store.commit('event/setUseCDNEndpoint', value);
};

const handleUseNewChatWidget = (value: boolean) => {
  loggerService.warn('USE_NEW_CHAT_APP changed to', value);
  store.commit('event/setUseNewChatWidget', value);
};

const handleUseNewNameCard = (value: boolean) => {
  loggerService.warn('Use_New_Name_Card changed to', value);
  store.commit('event/setUseNewNameCard', value);
};

const handleDisableChatEmoji = (value: boolean) => {
  loggerService.warn('DISABLE_CHAT_EMOJI changed to', value);
  store.commit('event/setDisableChatEmoji', value);
};

const handleEnablePushSpeakerToStage = (value: boolean) => {
  loggerService.warn('Enable_Push_Speaker_To_Stage changed to', value);
  store.commit('event/setEnablePushSpeakerToStage', value);
};

const handleEnableSpeakerReorder = (value: boolean) => {
  loggerService.warn('Enable_Speaker_Reorder changed to', value);
  store.commit('event/setEnableSpeakerReorder', value);
};

const handleUseBitrateCap = (value: boolean) => {
  loggerService.warn('Use_Bitrate_Cap changed to', value);
  store.commit('event/setUseBitrateCap', value);
};

const handleUseNewROSCues = (value: boolean) => {
  loggerService.warn('Use_New_ROS_Cues changed to', value);
  store.commit('event/setUseNewROSCues', value);
};

const handleUseOnDemandMQL = (value: boolean) => {
  loggerService.warn('Use_OnDemand_MQL changed to', value);
  store.commit('event/setUseOnDemandMQL', value);
};

const handleUseNewSlideAnimation = (value: boolean) => {
  loggerService.warn('USE_NEW_SLIDE_ANIMATION changed to', value);
  store.commit('event/setUseNewSlideAnimation', value);
};

const handleEnableRemoveFromBackstage = (value: boolean) => {
  loggerService.warn('Enable_Remove_From_Backstage changed to', value);
  store.commit('event/setEnableRemoveFromBackstage', value);
};

const handleUseFloatingEmoji = (value: boolean) => {
  loggerService.warn('Use_Floating_Emoji changed to', value);
  store.commit('event/setUseFloatingEmoji', value);
};

const handleUseNewAttendeeControlBar = (value: boolean) => {
  loggerService.info('Use_New_Attendee_Control_Bar changed to', value);
  store.commit('event/setUseNewAttendeeControlBar', value);
};

const handleUseCatchMeUp = (value: boolean) => {
  loggerService.warn('Use_Catch_Me_Up changed to', value);
  store.commit('event/setUseCatchMeUp', value);
};

const handleEnableBanAttendeeFromEvent = (value: boolean) => {
  loggerService.info('Enable_Ban_Attendee_From_Event changed to', value);
  store.commit('event/setEnableBanAttendeeFromEvent', value);
};

const handleNewLayoutsEnabled = (value: boolean) => {
  loggerService.info('Use_New_Layouts changed to', value);
  store.commit('event/setUseNewLayouts', value);
};

const handleUseCLPlatformBrandkit = (value: boolean) => {
  loggerService.info('Use_CL_Platform_Brandkit changed to', value);
  store.commit('event/setUsePlatformBrandKit', value);
};

const handleUseNewMuteUnmuteCapabilities = (value: boolean) => {
  loggerService.info('Use_New_Mute_Unmute_Capabilities changed to', value);
  store.commit('event/setUseNewMuteUnmuteCapabilities', value);
};

const handleUseNewDenoiserV2Change = (value: boolean) => {
  loggerService.info('Use_New_Denoiser_V2 changed to', value);
  store.commit('event/setUseNewDenoiserV2', value);
};

const handleUseRecorderSlides = (value: boolean) => {
  loggerService.info('Use_Recorder_Slides changed to', value);
  store.commit('event/setUseRecorderSlides', value);
};

const handleEventAPIConsolidation = (value: boolean) => {
  loggerService.info('EVENT_API_CONSOLIDATION changed to', value);
  store.commit('event/setEventApiConsolidation', value);
};

const handleMmApiConsolidation = (value: boolean) => {
  loggerService.info('MM_API_CONSOLIDATION changed to', value);
  store.commit('event/setMmApiConsolidation', value);
};

const handleUseAutoAudioRecover = (value: boolean) => {
  loggerService.warn('Use_Auto_Audio_Recover changed to', value);
  store.commit('event/setUseAutoAudioRecover', value);
};

const handleUseOptimizeRSCloudRecording = (value: boolean) => {
  loggerService.warn('USE_OPTIMIZE_RS_CLOUD_RECORDING changed to', value);
  store.commit('event/setUseOptimizeRSCloudRecording', value);
};

const handleUseRSNewLayouts = (value: boolean) => {
  loggerService.info('Use_RS_New_Layouts changed to', value);
  store.commit('event/setUseRSNewLayouts', value);
};

const handleUseRSAnimatedSlides = (value: boolean) => {
  loggerService.warn('USE_RS_ANIMATED_SLIDES changed to', value);
  store.commit('event/setUseRSAnimatedSlides', value);
};

const handleUseAgoraDatadog = (value: boolean) => {
  loggerService.info('USE_AGORA_DATADOG changed to', value);
  store.commit('event/setUseAgoraDatadog', value);
};

const handleEnableSessionLimit = (value: boolean) => {
  loggerService.warn('enable_session_limit changed to', value);
  store.commit('event/setEnableSessionLimit', value);
};

const handleRedirectUserToFailOverURL = (value: boolean) => {
  loggerService.info('Redirect_User_To_FailOver_URL changed to', value);
  store.commit('event/setRedirectUserToFailOverURL', value);
};

const handleUseSlowChat = (value: boolean) => {
  loggerService.info('Use_Slow_Chat changed to', value);
  store.commit('event/setUseSlowChat', value);
};

const handleSlowChatBlacklistedOrg = (value: boolean) => {
  loggerService.info('slow_chat_blacklisted_org changed to', value);
  store.commit('event/setSlowChatBlacklistedOrg', value);
};

export enum FeatureFlagKeys {
  USE_PRE_PROD = 'USE_PRE_PROD',
  use_inhouse_simulive = 'use_inhouse_simulive',
  Use_PPT_Animations = 'Use_PPT_Animations',
  Use_Hls_Guard = 'Use_Hls_Guard',
  Use_Speaker_Activity = 'Use_Speaker_Activity',
  isEventHeartbeatFastEnabled = 'isEventHeartbeatFastEnabled',
  Use_Recording_Play_Pause = 'Use_Recording_Play_Pause',
  useEventLocaleForDateTimeFormatting = 'useEventLocaleForDateTimeFormatting',
  Use_Pentest_flow = 'Use_Pentest_flow',
  Use_Hide_Sidebar = 'Use_Hide_Sidebar',
  USE_1080_VIDEO_QUALITY = 'USE_1080_VIDEO_QUALITY',
  enableInhouseAddToCalendar = 'enableInhouseAddToCalendar',
  Use_Cloud_Proxy_Flow = 'Use_Cloud_Proxy_Flow',
  Use_CDN_Endpoint = 'Use_CDN_Endpoint',
  USE_NEW_CHAT_APP = 'USE_NEW_CHAT_APP',
  Use_New_Name_Card = 'Use_New_Name_Card',
  DISABLE_CHAT_EMOJI = 'DISABLE_CHAT_EMOJI',
  Enable_Push_Speaker_To_Stage = 'Enable_Push_Speaker_To_Stage',
  Enable_Speaker_Reorder = 'Enable_Speaker_Reorder',

  Use_Bitrate_Cap = 'Use_Bitrate_Cap',
  Use_New_ROS_Cues = 'Use_New_ROS_Cues',
  USE_NEW_SLIDE_ANIMATION = 'USE_NEW_SLIDE_ANIMATION',
  Enable_Remove_From_Backstage = 'Enable_Remove_From_Backstage',
  Use_Floating_Emoji = 'Use_Floating_Emoji',
  Use_New_Attendee_Control_Bar = 'Use_New_Attendee_Control_Bar',
  Enable_Ban_Attendee_From_Event = 'Enable_Ban_Attendee_From_Event',
  Use_Catch_Me_Up = 'Use_Catch_Me_Up',
  Use_New_Layouts = 'Use_New_Layouts',
  Use_CL_Platform_Brandkit = 'Use_CL_Platform_Brandkit',
  Use_New_Mute_Unmute_Capabilities = 'Use_New_Mute_Unmute_Capabilities',
  Use_New_Denoiser_V2 = 'Use_New_Denoiser_V2',
  Use_OnDemand_MQL = 'Use_OnDemand_MQL',
  Use_Recorder_Slides = 'Use_Recorder_Slides',
  EVENT_API_CONSOLIDATION = 'EVENT_API_CONSOLIDATION',
  MM_API_CONSOLIDATION = 'MM_API_CONSOLIDATION',
  Use_Auto_Audio_Recover = 'Use_Auto_Audio_Recover',
  USE_OPTIMIZE_RS_CLOUD_RECORDING = 'USE_OPTIMIZE_RS_CLOUD_RECORDING',
  Use_Skip_480 = 'Use_Skip_480',
  Use_RS_New_Layouts = 'Use_RS_New_Layouts',
  USE_RS_ANIMATED_SLIDES = 'USE_RS_ANIMATED_SLIDES',
  USE_AGORA_DATADOG = 'USE_AGORA_DATADOG',
  enable_session_limit = 'enable_session_limit',
  Redirect_User_To_FailOver_URL = 'Redirect_User_To_FailOver_URL',
  Use_Slow_Chat = 'Use_Slow_Chat',
  slow_chat_blacklisted_org = 'slow_chat_blacklisted_org'
}

export type EventAppFeatureFlags = {
  [key in FeatureFlagKeys]: FeatureFlag;
};

export const featureFlags: EventAppFeatureFlags = {
  [FeatureFlagKeys.USE_PRE_PROD]: {
    value: true,
    changeHandler: handlePreProdChange
  },
  [FeatureFlagKeys.use_inhouse_simulive]: {
    value: false,
    changeHandler: handleInHouseSimuliveChange
  },

  [FeatureFlagKeys.Use_PPT_Animations]: {
    value: false,
    changeHandler: changeUsePPTAnimations
  },
  [FeatureFlagKeys.Use_Hls_Guard]: {
    value: false,
    changeHandler: handleUseHlsGuard
  },
  [FeatureFlagKeys.Use_Speaker_Activity]: {
    value: false,
    changeHandler: handleUseSpeakerActivity
  },
  [FeatureFlagKeys.isEventHeartbeatFastEnabled]: {
    value: false,
    changeHandler: handleEventHeartbeatFastEnabled
  },
  [FeatureFlagKeys.Use_Recording_Play_Pause]: {
    value: false,
    changeHandler: handleUseRecordingPlayPause
  },
  [FeatureFlagKeys.useEventLocaleForDateTimeFormatting]: {
    value: false,
    changeHandler: handleUseEventLocaleForDateTimeFormattingChange
  },
  [FeatureFlagKeys.Use_Pentest_flow]: {
    value: false,
    changeHandler: handleUsePentestFlow
  },
  [FeatureFlagKeys.Use_Hide_Sidebar]: {
    value: false,
    changeHandler: handleUseHideSidebar
  },
  [FeatureFlagKeys.USE_1080_VIDEO_QUALITY]: {
    value: false,
    changeHandler: handleUse1080VideoQuality
  },
  [FeatureFlagKeys.enableInhouseAddToCalendar]: {
    value: false,
    changeHandler: handleEnableInhouseAddToCalendar
  },
  [FeatureFlagKeys.Use_Cloud_Proxy_Flow]: {
    value: false,
    changeHandler: handleUseCloudProxyFlow
  },
  [FeatureFlagKeys.Use_CDN_Endpoint]: {
    value: false,
    changeHandler: handleUseCDNEndpoint
  },
  [FeatureFlagKeys.USE_NEW_CHAT_APP]: {
    value: false,
    changeHandler: handleUseNewChatWidget
  },
  [FeatureFlagKeys.Use_New_Name_Card]: {
    value: false,
    changeHandler: handleUseNewNameCard
  },
  [FeatureFlagKeys.DISABLE_CHAT_EMOJI]: {
    value: false,
    changeHandler: handleDisableChatEmoji
  },
  [FeatureFlagKeys.Enable_Push_Speaker_To_Stage]: {
    value: false,
    changeHandler: handleEnablePushSpeakerToStage
  },
  [FeatureFlagKeys.Enable_Speaker_Reorder]: {
    value: false,
    changeHandler: handleEnableSpeakerReorder
  },

  [FeatureFlagKeys.Use_Bitrate_Cap]: {
    value: false,
    changeHandler: handleUseBitrateCap
  },
  [FeatureFlagKeys.Use_New_ROS_Cues]: {
    value: false,
    changeHandler: handleUseNewROSCues
  },
  [FeatureFlagKeys.USE_NEW_SLIDE_ANIMATION]: {
    value: false,
    changeHandler: handleUseNewSlideAnimation
  },
  [FeatureFlagKeys.Enable_Remove_From_Backstage]: {
    value: false,
    changeHandler: handleEnableRemoveFromBackstage
  },
  [FeatureFlagKeys.Use_Floating_Emoji]: {
    value: false,
    changeHandler: handleUseFloatingEmoji
  },
  [FeatureFlagKeys.Use_New_Attendee_Control_Bar]: {
    value: false,
    changeHandler: handleUseNewAttendeeControlBar
  },
  [FeatureFlagKeys.Enable_Ban_Attendee_From_Event]: {
    value: false,
    changeHandler: handleEnableBanAttendeeFromEvent
  },
  [FeatureFlagKeys.Use_Catch_Me_Up]: {
    value: false,
    changeHandler: handleUseCatchMeUp
  },
  [FeatureFlagKeys.Use_New_Layouts]: {
    value: false,
    changeHandler: handleNewLayoutsEnabled
  },
  [FeatureFlagKeys.Use_CL_Platform_Brandkit]: {
    value: false,
    changeHandler: handleUseCLPlatformBrandkit
  },
  [FeatureFlagKeys.Use_New_Mute_Unmute_Capabilities]: {
    value: false,
    changeHandler: handleUseNewMuteUnmuteCapabilities
  },
  [FeatureFlagKeys.Use_New_Denoiser_V2]: {
    value: false,
    changeHandler: handleUseNewDenoiserV2Change
  },
  [FeatureFlagKeys.Use_OnDemand_MQL]: {
    value: false,
    changeHandler: handleUseOnDemandMQL
  },
  [FeatureFlagKeys.Use_Recorder_Slides]: {
    value: false,
    changeHandler: handleUseRecorderSlides
  },
  [FeatureFlagKeys.EVENT_API_CONSOLIDATION]: {
    value: false,
    changeHandler: handleEventAPIConsolidation
  },
  [FeatureFlagKeys.MM_API_CONSOLIDATION]: {
    value: false,
    changeHandler: handleMmApiConsolidation
  },
  [FeatureFlagKeys.Use_Auto_Audio_Recover]: {
    value: false,
    changeHandler: handleUseAutoAudioRecover
  },
  [FeatureFlagKeys.USE_OPTIMIZE_RS_CLOUD_RECORDING]: {
    value: false,
    changeHandler: handleUseOptimizeRSCloudRecording
  },
  [FeatureFlagKeys.Use_Skip_480]: {
    value: false,
    changeHandler: handleUseSkip480
  },
  [FeatureFlagKeys.Use_RS_New_Layouts]: {
    value: false,
    changeHandler: handleUseRSNewLayouts
  },
  [FeatureFlagKeys.USE_RS_ANIMATED_SLIDES]: {
    value: false,
    changeHandler: handleUseRSAnimatedSlides
  },
  [FeatureFlagKeys.USE_AGORA_DATADOG]: {
    value: false,
    changeHandler: handleUseAgoraDatadog
  },
  [FeatureFlagKeys.enable_session_limit]: {
    value: false,
    changeHandler: handleEnableSessionLimit
  },
  [FeatureFlagKeys.Redirect_User_To_FailOver_URL]: {
    value: false,
    changeHandler: handleRedirectUserToFailOverURL
  },
  [FeatureFlagKeys.Use_Slow_Chat]: {
    value: false,
    changeHandler: handleUseSlowChat
  },
  [FeatureFlagKeys.slow_chat_blacklisted_org]: {
    value: false,
    changeHandler: handleSlowChatBlacklistedOrg
  }
};

window.featureFlag = featureFlags as any;

// Store the initialization promise along with its streaming config.
let initState: { promise: Promise<void>; enableStreaming: boolean } | null = null;
const MAX_RETRIES = 3;
const BASE_DELAY = 500;
let setTimeoutVal: ReturnType<typeof setTimeout> | null = null;

export const initializeFeatureFlags = (enableStreaming = true, retryCount = 0): Promise<void> => {
  // Reuse an existing init when the streaming config matches, or when a
  // non-streaming caller can ride on an in-flight streaming init. Re-initing over
  // a pending init detaches its listeners and orphans that promise, which would
  // hang awaiters like enterEvent's Promise.all.
  if (initState?.promise && (initState.enableStreaming === enableStreaming || !enableStreaming)) {
    return initState.promise;
  }

  initState = null;
  setTimeoutVal && clearTimeout(setTimeoutVal);

  const promise = new Promise<void>((resolve, reject) => {
    const sessionId: string = generatePersistentUUID();
    const eventOrg = store.state.event.event?.organization;

    // If we don't have organization data, wait for a short time and try again
    if (!eventOrg && retryCount < MAX_RETRIES) {
      const delay = Math.pow(2, retryCount) * BASE_DELAY;
      loggerService.info('Waiting for organization data before initializing feature flags');
      setTimeoutVal = setTimeout(() => {
        // Clear the current state so we can try again
        initState = null;
        // Retry initialization
        initializeFeatureFlags(enableStreaming, retryCount + 1)
          .then(resolve)
          .catch(reject);
      }, delay);
      return;
    }

    init(
      window.configs.HARNESS_SECRET,
      {
        identifier: sessionId,
        name: store.state.user.currentEventUser?.name,
        attributes: {
          role: store.state.user.currentEventUser?.event_role,
          event: store.state.event.event?.id,
          eventOrganization: store.state.event.event?.organization,
          id: store.state.user.currentUser?.id || 'anonymous',
          app_env: APP_ENVIRONMENT
        }
      },
      featureFlags,
      {
        ready: flags => {
          Object.keys(flags).forEach(flagKey => {
            loggerService.addFeatureFlag(flagKey, String(flags[flagKey]));
          });
          resolve();
        },
        change: changeInfo => {
          loggerService.addFeatureFlag(changeInfo.flag, String(changeInfo.value));
          if (window.featureFlag[changeInfo?.flag]) {
            window.featureFlag[changeInfo?.flag].value = changeInfo.value;
            window.featureFlag[changeInfo?.flag].changeHandler?.(changeInfo.value);
          }
        },
        errored: error => {
          loggerService.error('Feature flags errored:', error);
          reject(error);
        },
        errorAuth: error => {
          loggerService.error('Feature flags auth error:', error);
          reject(error);
        }
      },
      { enableStreaming }
    );
  });

  initState = { promise, enableStreaming };

  return promise;
};
